import { cn } from '@/utils/index';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';

import type { VariantProps } from 'class-variance-authority';
import { LoaderCircle } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-button text-body-m-regular ring-offset-fill-background-neutral transition-colors disabled:bg-fill-controls-disabled disabled:text-text-disabled disabled:[&>svg]:text-icon-disabled disabled:pointer-events-none focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2',
  {
    variants: {
      size: {
        m: 'px-5 py-2 text-body-s-regular',
        l: 'px-5 py-2.5 text-body-m-regular',
        'icon-m': 'p-2.5 [&>svg]:size-5',
        'icon-l': 'p-2.5 [&>svg]:size-6',
        box: 'p-5 h-full [&>svg]:size-6 rounded-xl [&_svg]:stroke-text-brand-primary [&>svg:last-of-type]:stroke-text-default-secondary text-body-m-semibold justify-between',
      },
      variant: {
        default:
          'bg-fill-button-primary-default text-text-default-white [&>svg]:text-text-default-white hover:bg-fill-button-primary-hover active:bg-fill-button-primary-on-click',
        secondary:
          'bg-fill-button-secondary-default text-text-default-primary [&>svg]:text-text-default-primary hover:bg-fill-button-secondary-hover active:bg-fill-button-secondary-on-click',
        'text-primary':
          'bg-transparent text-text-brand-primary [&>svg]:text-text-brand-primary hover:text-text-brand-hover hover:[&>svg]:text-text-brand-hover',
        'text-secondary':
          'bg-transparent text-text-default-secondary [&>svg]:text-text-default-secondary hover:text-text-default-primary hover:[&>svg]:text-text-default-primary',
        destructive:
          'bg-fill-button-destructive-default text-text-default-white [&>svg]:text-text-default-white hover:bg-fill-button-destructive-hover active:bg-fill-button-destructive-on-click',
        icon: 'hover:bg-fill-button-action-icon-hover active:bg-fill-button-action-icon-on-click',
        link: 'text-text-default-primary hover:underline disabled:underline decoration-[10%] disabled:bg-transparent',
      },
    },
    compoundVariants: [
      {
        variant: 'link',
        size: 'm',
        className: 'text-link-s-regular',
      },
      {
        variant: 'link',
        size: 'l',
        className: 'text-link-m-regular',
      },
      {
        variant: 'secondary',
        size: 'box',
        className: 'bg-fill-background-light',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'l',
    },
  },
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}

const Button = ({ className, variant, size, children, asChild = false, isLoading = false, ref, ...props }: ButtonProps) => {
  const { t } = useTranslation('components', {
    keyPrefix: 'button',
  });

  const Comp = asChild ? Slot : 'button';

  return (
    <Comp ref={ref} className={cn(buttonVariants({ variant, size, className }))} aria-busy={isLoading ? true : undefined} {...props}>
      {isLoading && <LoaderCircle aria-label={t('loader.ariaLabel')} size={20} className='animate-spin' strokeWidth={1.5} />}
      <Slottable>{children}</Slottable>
    </Comp>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
